import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Divider,
  Stack,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Select,
  InputLabel,
  FormControl,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
  Psychology as StrategyIcon,
  Rule as RuleIcon,
  AccountTree,
  Code as CodeIcon
} from '@mui/icons-material';
import api from '../../services/api';
import RuleJsonEditor from './RuleJsonEditor';
import VisualRuleBuilder from './triage-rule-builder/VisualRuleBuilder';

const TriageStrategyManager = () => {
  const [strategies, setStrategies] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(() => {
    const saved = localStorage.getItem('triageStrategies_includeInactive');
    return saved ? JSON.parse(saved) : false;
  });

  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [editTabValue, setEditTabValue] = useState(0);
  const [strategyUsage, setStrategyUsage] = useState(null);

  // Clone form
  const [cloneName, setCloneName] = useState('');
  const [cloneKey, setCloneKey] = useState('');

  // Rules editor mode
  const [rulesEditorMode, setRulesEditorMode] = useState('visual');

  // Edit form (includes rules now)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    defaultMode: 'automatic',
    fallbackPersona: '',
    isActive: true,
    rules: []
  });

  // Create form
  const [createForm, setCreateForm] = useState({
    name: '',
    key: '',
    description: '',
    defaultMode: 'automatic',
    fallbackPersona: 'generalist',
    isActive: false,
    rules: []
  });

  useEffect(() => {
    fetchData();
  }, [includeInactive]);

  useEffect(() => {
    localStorage.setItem('triageStrategies_includeInactive', JSON.stringify(includeInactive));
  }, [includeInactive]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [strategiesRes, personasRes] = await Promise.all([
        api.get(`/admin/triage-strategies?includeInactive=${includeInactive}`),
        api.get('/personas')
      ]);

      setStrategies(strategiesRes.data.strategies || []);
      setPersonas(personasRes.data.personas || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.msg || 'Failed to load strategies');
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    // Create with a default rule (schema requires at least one rule)
    // Note: The Mongoose schema requires 'operator' on criteria and 'reason' on action
    const defaultPersona = personas[0]?.key || 'generalist';
    const defaultRule = {
      priority: 1,
      name: 'Default Rule',
      description: 'Default rule - update conditions and action',
      isActive: true,
      conditions: {
        operator: 'single',
        criteria: {
          type: 'flag_present',
          domain: 'depression',
          operator: '==',  // Required by Mongoose schema
          value: true
        }
      },
      action: {
        type: 'assign_persona',
        personaKey: defaultPersona,
        reason: 'Default persona assignment - update as needed'  // Required by Mongoose schema
      }
    };

    setCreateForm({
      name: '',
      key: '',
      description: '',
      defaultMode: 'automatic',
      fallbackPersona: defaultPersona,
      isActive: false,
      rules: [defaultRule]
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSubmit = async () => {
    try {
      await api.post('/admin/triage-strategies', createForm);
      setSuccess('Strategy created successfully');
      setCreateDialogOpen(false);
      setCreateForm({
        name: '',
        key: '',
        description: '',
        defaultMode: 'automatic',
        fallbackPersona: 'generalist',
        isActive: false,
        rules: []
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to create strategy');
    }
  };

  const handleView = (strategy) => {
    setSelectedStrategy(strategy);
    setViewDialogOpen(true);
  };

  const handleEdit = async (strategy) => {
    setSelectedStrategy(strategy);
    setEditForm({
      name: strategy.name,
      description: strategy.description || '',
      defaultMode: strategy.defaultMode,
      fallbackPersona: strategy.fallbackPersona,
      isActive: strategy.isActive,
      rules: strategy.rules || []
    });
    setEditTabValue(0); // Reset to first tab

    // Check if strategy is in use
    try {
      const response = await api.get(`/admin/triage-strategies/${strategy._id}/usage-check`);
      setStrategyUsage(response.data);
    } catch (err) {
      console.error('Error checking usage:', err);
      setStrategyUsage(null);
    }

    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await api.put(`/admin/triage-strategies/${selectedStrategy._id}`, editForm);
      setSuccess('Strategy updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update strategy');
    }
  };

  const handleClone = (strategy) => {
    setSelectedStrategy(strategy);
    setCloneName(`${strategy.name} (Copy)`);
    setCloneKey(`${strategy.key}-copy-${Date.now()}`);
    setCloneDialogOpen(true);
  };

  const handleCloneSubmit = async () => {
    try {
      await api.post(`/admin/triage-strategies/${selectedStrategy._id}/clone`, {
        newName: cloneName,
        newKey: cloneKey
      });
      setSuccess('Strategy cloned successfully');
      setCloneDialogOpen(false);
      setCloneName('');
      setCloneKey('');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to clone strategy');
    }
  };

  const handleDelete = async (strategy) => {
    if (!window.confirm(`Are you sure you want to deactivate "${strategy.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/admin/triage-strategies/${strategy._id}`);
      setSuccess('Strategy deactivated successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to deactivate strategy');
    }
  };

  const handleToggleActive = async (strategy) => {
    try {
      await api.put(`/admin/triage-strategies/${strategy._id}`, {
        isActive: !strategy.isActive
      });
      setSuccess(`Strategy ${!strategy.isActive ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to update strategy');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <StrategyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Triage Strategies
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label="Show Inactive"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure routing rules for assigning AI therapist personas based on screening assessments
      </Typography>

      <Box sx={{ my: 2 }}>
        <Alert severity="info">
          <strong>{strategies.length}</strong> strateg{strategies.length !== 1 ? 'ies' : 'y'} configured
        </Alert>
      </Box>

      {strategies.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No triage strategies found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Rules</TableCell>
                <TableCell>Fallback Persona</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.map((strategy) => (
                <TableRow key={strategy._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {strategy.name}
                    </Typography>
                    {strategy.description && (
                      <Typography variant="caption" color="text.secondary">
                        {strategy.description.substring(0, 60)}
                        {strategy.description.length > 60 ? '...' : ''}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip label={strategy.key} size="small" variant="outlined" />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={strategy.defaultMode}
                      size="small"
                      color={strategy.defaultMode === 'automatic' ? 'success' : 'warning'}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={<RuleIcon />}
                      label={`${strategy.rules?.length || 0} rules`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip label={strategy.fallbackPersona} size="small" />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={strategy.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={strategy.isActive ? 'success' : 'default'}
                      icon={strategy.isActive ? <ActiveIcon /> : <InactiveIcon />}
                    />
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleView(strategy)}
                        title="View Details"
                      >
                        <ViewIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(strategy)}
                        title="Edit Strategy"
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => handleClone(strategy)}
                        title="Clone Strategy"
                      >
                        <CloneIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        color={strategy.isActive ? 'warning' : 'success'}
                        onClick={() => handleToggleActive(strategy)}
                        title={strategy.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {strategy.isActive ? <InactiveIcon /> : <ActiveIcon />}
                      </IconButton>

                      {strategy.key !== 'default-triage-strategy' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(strategy)}
                          title="Delete Strategy"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            <StrategyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Strategy Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedStrategy && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                  <Typography variant="body1">{selectedStrategy.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Key</Typography>
                  <Typography variant="body1">{selectedStrategy.key}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Mode</Typography>
                  <Chip label={selectedStrategy.defaultMode} size="small" />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Fallback Persona</Typography>
                  <Chip label={selectedStrategy.fallbackPersona} size="small" />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedStrategy.description || 'No description'}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Rules ({selectedStrategy.rules?.length || 0})</Typography>

              {selectedStrategy.rules && selectedStrategy.rules.map((rule, idx) => (
                <Accordion key={idx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Chip label={`Priority ${rule.priority}`} size="small" color="primary" />
                      <Typography fontWeight="bold">{rule.name}</Typography>
                      <Chip
                        label={rule.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={rule.isActive ? 'success' : 'default'}
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                        <Typography variant="body2">{rule.description}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                        <Chip label={rule.action.type} size="small" color="secondary" />
                        {rule.action.personaKey && (
                          <Chip label={rule.action.personaKey} size="small" sx={{ ml: 1 }} />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
                        <Typography variant="body2">{rule.action.reason}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Conditions</Typography>
                        <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                          <pre style={{ fontSize: '12px', margin: 0, overflowX: 'auto' }}>
                            {JSON.stringify(rule.conditions, null, 2)}
                          </pre>
                        </Paper>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog with Tabs */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Strategy</DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={editTabValue} onChange={(e, v) => setEditTabValue(v)}>
            <Tab label="Basic Info" />
            <Tab label="Rules" />
          </Tabs>
        </Box>
        <DialogContent dividers>
          {/* Usage Warning */}
          {strategyUsage?.isInUse && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Strategy In Use</AlertTitle>
              This strategy is currently used by <strong>{strategyUsage.usageCount}</strong> active onboarding flow(s):
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                {strategyUsage.flows.map((flow, idx) => (
                  <li key={idx}>{flow.name}</li>
                ))}
              </Box>
              Changes to rules will affect these flows immediately.
            </Alert>
          )}

          {/* Protect default strategy */}
          {selectedStrategy?.key === 'default-triage-strategy' && editTabValue === 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Default Strategy</AlertTitle>
              This is the default triage strategy. Consider cloning it instead of modifying the rules directly.
            </Alert>
          )}

          {/* Tab 1: Basic Info */}
          {editTabValue === 0 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />

              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />

              <FormControl fullWidth>
                <InputLabel>Default Mode</InputLabel>
                <Select
                  value={editForm.defaultMode}
                  onChange={(e) => setEditForm({ ...editForm, defaultMode: e.target.value })}
                  label="Default Mode"
                >
                  <MenuItem value="automatic">Automatic</MenuItem>
                  <MenuItem value="manual_review">Manual Review</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Fallback Persona</InputLabel>
                <Select
                  value={editForm.fallbackPersona}
                  onChange={(e) => setEditForm({ ...editForm, fallbackPersona: e.target.value })}
                  label="Fallback Persona"
                >
                  {personas.map((persona) => (
                    <MenuItem key={persona.key} value={persona.key}>
                      {persona.name} ({persona.key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Stack>
          )}

          {/* Tab 2: Rules Editor */}
          {editTabValue === 1 && selectedStrategy && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <ToggleButtonGroup
                  value={rulesEditorMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setRulesEditorMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="visual">
                    <AccountTree sx={{ mr: 0.5 }} fontSize="small" />
                    Visual
                  </ToggleButton>
                  <ToggleButton value="json">
                    <CodeIcon sx={{ mr: 0.5 }} fontSize="small" />
                    JSON
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {rulesEditorMode === 'visual' && (
                <VisualRuleBuilder
                  rules={editForm.rules}
                  onChange={(newRules) => setEditForm({ ...editForm, rules: newRules })}
                  strategyId={selectedStrategy._id}
                  fallbackPersona={editForm.fallbackPersona}
                />
              )}

              {rulesEditorMode === 'json' && (
                <RuleJsonEditor
                  rules={editForm.rules}
                  onChange={(newRules) => setEditForm({ ...editForm, rules: newRules })}
                  strategyId={selectedStrategy._id}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Strategy</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="New Strategy Name"
              fullWidth
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              required
            />

            <TextField
              label="New Strategy Key"
              fullWidth
              value={cloneKey}
              onChange={(e) => setCloneKey(e.target.value)}
              helperText="Must be unique, lowercase, hyphens only"
              required
            />

            <Alert severity="info">
              This will create an exact copy of all rules. The clone will start as inactive.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCloneSubmit}
            variant="contained"
            color="secondary"
            disabled={!cloneName || !cloneKey}
          >
            Clone Strategy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Strategy Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Strategy</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Strategy Name"
              fullWidth
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              required
              helperText="A descriptive name for this triage strategy"
            />

            <TextField
              label="Strategy Key"
              fullWidth
              value={createForm.key}
              onChange={(e) => setCreateForm({ ...createForm, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
              helperText="Unique identifier (lowercase, hyphens only)"
              required
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Default Mode</InputLabel>
              <Select
                value={createForm.defaultMode}
                onChange={(e) => setCreateForm({ ...createForm, defaultMode: e.target.value })}
                label="Default Mode"
              >
                <MenuItem value="automatic">Automatic</MenuItem>
                <MenuItem value="manual_review">Manual Review</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Fallback Persona</InputLabel>
              <Select
                value={createForm.fallbackPersona}
                onChange={(e) => setCreateForm({ ...createForm, fallbackPersona: e.target.value })}
                label="Fallback Persona"
              >
                {personas.map((persona) => (
                  <MenuItem key={persona.key} value={persona.key}>
                    {persona.name} ({persona.key})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info">
              The new strategy will be created as <strong>inactive</strong> with a default rule. You can edit the rules and activate it after creation.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            color="primary"
            disabled={!createForm.name || !createForm.key}
          >
            Create Strategy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TriageStrategyManager;
